/**
 * PDP / quick-add: resolve latest open ddcs_term by rental_end_date among terms
 * where this product is adopted (section_data_mapping + ddcs_section). Store-wide
 * farthest open term is only used when there is no adoption payload.
 * Then matching ddcs campus + term rows (getFilteredCampusTerm + metaobject fallbacks).
 *
 * Caches catalogs on window for Follett Access program-name joins:
 *   __pdpDdcsOpenTerms, __pdpDdcsCampuses, __pdpDdcsTerms
 *
 * Exposes cart line properties aligned with CMRP (product-facade getCmrpRentalCampusTermProperties):
 * _rental_refund_grace_period ← campus.rental_refund_grace_period
 * _rental_refund_date ← term.rental_refund_date
 * Callers append both keys even when values are empty strings (term/campus missing field).
 *
 * product-form.js and dialog.js await __pdpDdcsRentalReadyPromise before add-to-cart for rentals.
 */
(function () {
  'use strict';

  /** @type {(value?: unknown) => void} */
  let resolveDdcsRentalReady = () => {};
  window.__pdpDdcsRentalReadyPromise = new Promise((resolve) => {
    resolveDdcsRentalReady = resolve;
  });

  /**
   * @param {any} row
   * @param {string[]} keys
   * @returns {unknown}
   */
  function firstDefinedField(row, keys) {
    if (!row || typeof row !== 'object') return undefined;
    for (const k of keys) {
      if (k in row && row[k] != null && String(row[k]).trim() !== '') {
        return row[k];
      }
    }
    const f = row.fields;
    if (f && typeof f === 'object') {
      for (const k of keys) {
        if (k in f && f[k] != null && String(f[k]).trim() !== '') {
          return f[k];
        }
      }
    }
    return undefined;
  }

  /**
   * @param {any} campus
   * @returns {unknown}
   */
  function getCampusRentalRefundGracePeriod(campus) {
    return firstDefinedField(campus, [
      'rental_refund_grace_period',
      'rentalRefundGracePeriod',
      'rental_refund_grace',
    ]);
  }

  /**
   * @param {any} term
   * @returns {unknown}
   */
  function getTermRentalRefundDate(term) {
    return firstDefinedField(term, ['rental_refund_date', 'rentalRefundDate']);
  }

  /**
   * getFilteredCampusTerm often returns slim rows; the "latest term" from metaobjects is full.
   * When both refer to the same term_id, copy refund date from latest if the resolved row lacks it.
   * If no row matched the filter, fall back to latestTerm (still the correct open term for PDP).
   * @param {any | null} termRow
   * @param {any | null} latestTerm
   * @returns {any | null}
   */
  function resolveTermRowForRentalRefund(termRow, latestTerm) {
    if (!latestTerm) return termRow ?? null;
    const latestId = normId(latestTerm.term_id);
    if (!termRow) {
      return latestTerm;
    }
    const same =
      normId(termRow.term_id) === latestId ||
      normId(termRow.term) === latestId;
    if (!same) return termRow;
    const fromRow = getTermRentalRefundDate(termRow);
    if (fromRow != null && String(fromRow).trim() !== '') return termRow;
    const fromLatest = getTermRentalRefundDate(latestTerm);
    if (fromLatest == null || String(fromLatest).trim() === '') return termRow;
    return { ...termRow, rental_refund_date: fromLatest };
  }

  /**
   * Same property names as CMRP add-to-cart (product-facade.js).
   * @returns {{ _rental_refund_grace_period: string, _rental_refund_date: string }}
   */
  function getPdpRentalCampusTermLineProperties() {
    const campus = window.__pdpDdcsCampusForRental;
    const term = window.__pdpDdcsTermForRental;
    const grace = getCampusRentalRefundGracePeriod(campus);
    const refundDate = getTermRentalRefundDate(term);
    const out = {
      _rental_refund_grace_period: grace != null && String(grace) !== '' ? String(grace) : '',
      _rental_refund_date:
        refundDate != null && String(refundDate) !== '' ? String(refundDate) : '',
    };
    return out;
  }
  window.getPdpRentalCampusTermLineProperties = getPdpRentalCampusTermLineProperties;

  const metaobjectsUrl = '/apps/shopifyData/metaobjects';
  const filteredCampusTermUrl = '/apps/shopifyData/getFilteredCampusTerm';

  /**
   * @param {string} v
   * @returns {string}
   */
  function normId(v) {
    return String(v ?? '')
      .trim()
      .toLowerCase();
  }

  /**
   * Same response handling as page.courses-materials-results.liquid (fetchMetaobjectsData).
   * @returns {Promise<{ campuses: any[]; terms: any[] }>}
   */
  async function fetchFilteredCampusTermLists() {
    const response = await fetch(filteredCampusTermUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) {
      throw new Error(`getFilteredCampusTerm HTTP ${response.status}`);
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'getFilteredCampusTerm failed');
    }
    const raw = result.data ?? result;
    let campuses = raw.ddcs_campus;
    let terms = raw.ddcs_term;
    if ((!Array.isArray(campuses) || campuses.length === 0) && raw.data && typeof raw.data === 'object') {
      campuses = raw.data.ddcs_campus ?? campuses;
      terms = raw.data.ddcs_term ?? terms;
    }
    return {
      campuses: Array.isArray(campuses) ? campuses : [],
      terms: Array.isArray(terms) ? terms : [],
    };
  }

  /**
   * @param {string} campusEntityId
   * @param {string} termId
   * @param {any[]} campuses
   * @param {any[]} terms
   * @returns {{ campus: any | null; term: any | null }}
   */
  function pickFromFilteredLists(campusEntityId, termId, campuses, terms) {
    const ce = normId(campusEntityId);
    const tid = normId(termId);
    const campus = campuses.find((c) => normId(c.campus_entity_id) === ce) ?? null;
    const term =
      terms.find((t) => normId(t.term_id) === tid || normId(t.term) === tid) ?? null;
    return { campus, term };
  }

  /**
   * @param {string} metaobjectType
   * @param {string} query
   * @returns {Promise<any[]>}
   */
  async function fetchMetaobjects(metaobjectType, query) {
    const url = `${metaobjectsUrl}?type=${metaobjectType}&query=${encodeURIComponent(query)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} (${metaobjectType})`);
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || `metaobjects failed (${metaobjectType})`);
    }
    const data = Array.isArray(result.data) ? result.data : [];
    const active = data.filter((item) => item.status === 'ACTIVE');
    if (active.length > 0) return active;
    /* Some payloads omit status or use non-ACTIVE; keep rows for debugging if search matched */
    return data;
  }

  /**
   * Product metafield ddcs.ddcs_campus_entity → definition handle is often ddcs_campus_entity, not ddcs_campus.
   * @param {string} campusEntityId
   * @returns {Promise<any | null>}
   */
  async function fetchDdcsCampusByEntityIdViaMetaobjects(campusEntityId) {
    const q = `fields.campus_entity_id:'${campusEntityId}' fields.disabled_flag:false`;
    for (const type of ['ddcs_campus_entity', 'ddcs_campus']) {
      try {
        const rows = await fetchMetaobjects(type, q);
        if (rows.length > 0) return rows[0];
      } catch {
        /* type may not exist on this shop */
      }
    }
    return null;
  }

  /**
   * url-state-manager findMatchingEntities + variant-textbook-picker style.
   * @param {string} termId
   * @returns {Promise<any | null>}
   */
  async function fetchDdcsTermByTermIdViaMetaobjects(termId) {
    const qOr = `(fields.term:'${termId}' OR fields.term_id:'${termId}') fields.disabled_flag:false fields.is_term_open_flag:true`;
    const rows = await fetchMetaobjects('ddcs_term', qOr);
    if (rows.length > 0) return rows[0];
    const qIdOnly = `fields.term_id:'${termId}' fields.disabled_flag:false fields.is_term_open_flag:true`;
    const rows2 = await fetchMetaobjects('ddcs_term', qIdOnly);
    return rows2[0] ?? null;
  }

  /**
   * @param {any[] | null | undefined} campuses
   * @param {any[] | null | undefined} terms
   */
  function cacheFilteredCampusTermLists(campuses, terms) {
    window.__pdpDdcsCampuses = Array.isArray(campuses) ? campuses : [];
    window.__pdpDdcsTerms = Array.isArray(terms) ? terms : [];
  }

  /**
   * @param {any[] | null | undefined} terms
   */
  function cacheOpenTerms(terms) {
    window.__pdpDdcsOpenTerms = Array.isArray(terms) ? terms : [];
  }

  /**
   * @param {unknown} flag
   * @returns {boolean}
   */
  function isTruthyFlag(flag) {
    return flag === true || String(flag).toLowerCase() === 'true';
  }

  /**
   * @param {unknown} a
   * @param {unknown} b
   * @returns {boolean}
   */
  function sameDdcsId(a, b) {
    return normId(a) !== '' && normId(a) === normId(b);
  }

  /**
   * @param {any} term
   * @returns {boolean}
   */
  function isActiveOpenTerm(term) {
    if (!term || typeof term !== 'object') return false;
    if (isTruthyFlag(term.disabled_flag ?? term.disabledFlag)) return false;
    const open = term.is_term_open_flag ?? term.isTermOpenFlag;
    if (open !== undefined && open !== null && String(open).trim() !== '') {
      return isTruthyFlag(open);
    }
    return true;
  }

  /**
   * @param {any[]} terms
   * @returns {any | null}
   */
  function pickFurthestRentalEndTerm(terms) {
    /** @type {any} */
    let latestTerm = null;
    let latestTs = -Infinity;
    for (const term of terms) {
      const end = term && term.rental_end_date;
      if (end == null || String(end).trim() === '') continue;
      const ts = new Date(end).getTime();
      if (Number.isNaN(ts)) continue;
      if (ts >= latestTs) {
        latestTs = ts;
        latestTerm = term;
      }
    }
    return latestTerm;
  }

  /**
   * APDP adoption payload from #pdp-follett-access-config (product metafields).
   * @returns {{ productId: string | null, sectionDataMapping: any[], ddcsSectionEntries: any[] }}
   */
  function readPdpAdoptionConfig() {
    const el = document.getElementById('pdp-follett-access-config');
    if (!el) {
      return { productId: null, sectionDataMapping: [], ddcsSectionEntries: [] };
    }
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      const mappingRaw = parsed.sectionDataMapping;
      const mapping = Array.isArray(mappingRaw) ? mappingRaw : [];
      const entriesRaw = parsed.ddcsSectionEntries;
      const entries = Array.isArray(entriesRaw) ? entriesRaw : [];
      return {
        productId: parsed.productId != null ? String(parsed.productId) : null,
        sectionDataMapping: mapping,
        ddcsSectionEntries: entries,
      };
    } catch {
      return { productId: null, sectionDataMapping: [], ddcsSectionEntries: [] };
    }
  }

  /**
   * @param {any[]} mapping
   * @returns {string[]}
   */
  function collectAdoptedSectionIdsFromMapping(mapping) {
    if (!Array.isArray(mapping)) return [];
    /** @type {Set<string>} */
    const ids = new Set();
    mapping.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const disabled = item.DisabledFlag ?? item.disabled_flag ?? item.disabledFlag;
      if (isTruthyFlag(disabled)) return;
      const sid = item.SectionID ?? item.section_id ?? item.sectionId;
      if (sid == null || String(sid).trim() === '') return;
      ids.add(String(sid).trim());
    });
    return [...ids];
  }

  /**
   * @param {unknown} termRaw
   * @returns {string}
   */
  function coerceTermId(termRaw) {
    if (termRaw == null || termRaw === '') return '';
    if (typeof termRaw === 'object') {
      const nested =
        termRaw.term_id ?? termRaw.termId ?? termRaw.value ?? termRaw.id ?? null;
      if (nested != null && typeof nested !== 'object') return String(nested).trim();
      return '';
    }
    return String(termRaw).trim();
  }

  /**
   * @param {any[]} entries
   * @param {string[]} mappingSectionIds
   * @returns {{ termIds: string[], missingSectionIds: string[] }}
   */
  function collectTermIdsFromSectionEntries(entries, mappingSectionIds) {
    /** @type {Set<string>} */
    const termIds = new Set();
    /** @type {Set<string>} */
    const covered = new Set();
    const restrict = mappingSectionIds.length > 0;

    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      if (isTruthyFlag(entry.disabled_flag ?? entry.disabledFlag)) return;
      const sectionId = String(
        entry.section_id ?? entry.sectionId ?? entry.SectionID ?? entry.gid ?? '',
      ).trim();
      if (restrict && sectionId && !mappingSectionIds.some((sid) => sameDdcsId(sid, sectionId))) {
        return;
      }
      const termId = coerceTermId(entry.term_id ?? entry.termId ?? entry.term_id_guid ?? null);
      if (termId) {
        termIds.add(termId);
        if (sectionId) covered.add(sectionId.toLowerCase());
      }
    });

    const missingSectionIds = restrict
      ? mappingSectionIds.filter((sid) => !covered.has(sid.toLowerCase()))
      : [];

    return { termIds: [...termIds], missingSectionIds };
  }

  /**
   * @param {string[]} sectionIds
   * @returns {Promise<string[]>}
   */
  async function fetchTermIdsForSectionsChunked(sectionIds) {
    if (!sectionIds.length) return [];
    const CHUNK = 20;
    /** @type {string[]} */
    const termIds = [];
    for (let i = 0; i < sectionIds.length; i += CHUNK) {
      const chunk = sectionIds.slice(i, i + CHUNK);
      const sectionOrQuery = chunk
        .map((id) => `(fields.section:'${id}' OR fields.section_id:'${id}')`)
        .join(' OR ');
      const sections = await fetchMetaobjects(
        'ddcs_section',
        `(${sectionOrQuery}) fields.disabled_flag:false`,
      );
      sections.forEach((s) => {
        const tid = s?.term_id;
        if (tid != null && String(tid).trim() !== '') termIds.push(String(tid).trim());
      });
    }
    return [...new Set(termIds)];
  }

  /**
   * @param {string[]} termIds
   * @param {any[]} termRows
   * @returns {any[]}
   */
  function filterTermsByAdoptedIds(termIds, termRows) {
    if (!Array.isArray(termRows) || termRows.length === 0 || termIds.length === 0) {
      return [];
    }
    return termRows.filter((t) => {
      if (!isActiveOpenTerm(t)) return false;
      const tid = t?.term_id ?? t?.term;
      return termIds.some((id) => sameDdcsId(id, tid));
    });
  }

  /**
   * @param {string[]} termIds
   * @returns {Promise<any[]>}
   */
  async function fetchTermsByIdsChunked(termIds) {
    if (!termIds.length) return [];
    const CHUNK = 20;
    /** @type {any[]} */
    const fetched = [];
    for (let i = 0; i < termIds.length; i += CHUNK) {
      const chunk = termIds.slice(i, i + CHUNK);
      const termOrQuery = chunk
        .map((id) => `(fields.term:'${id}' OR fields.term_id:'${id}')`)
        .join(' OR ');
      const rows = await fetchMetaobjects(
        'ddcs_term',
        `(${termOrQuery}) fields.disabled_flag:false`,
      );
      fetched.push(...rows.filter(isActiveOpenTerm));
    }
    return fetched;
  }

  /**
   * Fetch open terms, then pick furthest rental_end_date among terms where this
   * product is adopted (APDP). Store-wide farthest is only used when there is
   * no adoption payload (e.g. listing quick-add).
   * Caches the full open-term list on window for Access program-name resolution.
   * @returns {Promise<any | null>}
   */
  async function getLatestTermByRentalEndDate() {
    const termQuery = 'fields.disabled_flag:false fields.is_term_open_flag:true';
    const termUrl = `${metaobjectsUrl}?type=ddcs_term&query=${encodeURIComponent(termQuery)}`;

    const termResponse = await fetch(termUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    });

    if (!termResponse.ok) {
      cacheOpenTerms([]);
      return null;
    }

    const termResult = await termResponse.json();
    if (!termResult.success || !Array.isArray(termResult.data) || termResult.data.length === 0) {
      cacheOpenTerms([]);
      return null;
    }

    cacheOpenTerms(termResult.data);

    const storeWideLatest = pickFurthestRentalEndTerm(termResult.data);

    const adoption = readPdpAdoptionConfig();
    const mappingSectionIds = collectAdoptedSectionIdsFromMapping(adoption.sectionDataMapping);

    let { termIds, missingSectionIds } = collectTermIdsFromSectionEntries(
      adoption.ddcsSectionEntries,
      mappingSectionIds,
    );

    if (missingSectionIds.length > 0) {
      const fromSections = await fetchTermIdsForSectionsChunked(missingSectionIds);
      termIds = [...new Set([...termIds, ...fromSections])];
    } else if (termIds.length === 0) {
      const sectionIdsFromEntries = (adoption.ddcsSectionEntries || [])
        .filter((e) => e && !isTruthyFlag(e.disabled_flag ?? e.disabledFlag))
        .map((e) => String(e.section_id ?? e.sectionId ?? '').trim())
        .filter(Boolean);
      const toFetch = mappingSectionIds.length > 0 ? mappingSectionIds : sectionIdsFromEntries;
      if (toFetch.length > 0) {
        termIds = await fetchTermIdsForSectionsChunked(toFetch);
      }
    }

    if (termIds.length === 0) {
      return storeWideLatest;
    }

    let candidates = filterTermsByAdoptedIds(termIds, termResult.data);

    if (candidates.length === 0 || !candidates.some((t) => t?.rental_end_date)) {
      const fetched = await fetchTermsByIdsChunked(termIds);
      if (fetched.length > 0) candidates = fetched;
    }

    const adoptedLatest = pickFurthestRentalEndTerm(candidates);
    if (!adoptedLatest) {
      return null;
    }

    return adoptedLatest;
  }

  /**
   * @param {any} latestTerm
   */
  async function logResolvedDdcsMetaobjects(latestTerm) {
    const campusEntityId = latestTerm && latestTerm.campus_entity_id;
    const termId = latestTerm && latestTerm.term_id;
    if (!campusEntityId || !termId) {
      window.__pdpDdcsCampusForRental = null;
      window.__pdpDdcsTermForRental = null;
      return;
    }

    const cStr = String(campusEntityId);
    const tStr = String(termId);

    try {
      let campusRow = null;
      let termRow = null;
      /** @type {string} */
      let campusFrom = 'none';
      /** @type {string} */
      let termFrom = 'none';

      try {
        let campuses = Array.isArray(window.__pdpDdcsCampuses) ? window.__pdpDdcsCampuses : null;
        let terms = Array.isArray(window.__pdpDdcsTerms) ? window.__pdpDdcsTerms : null;
        if (!campuses || !terms) {
          const fetched = await fetchFilteredCampusTermLists();
          campuses = fetched.campuses;
          terms = fetched.terms;
          cacheFilteredCampusTermLists(campuses, terms);
        }
        const picked = pickFromFilteredLists(cStr, tStr, campuses, terms);
        if (picked.campus) {
          campusRow = picked.campus;
          campusFrom = 'getFilteredCampusTerm';
        }
        if (picked.term) {
          termRow = picked.term;
          termFrom = 'getFilteredCampusTerm';
        }
      } catch (e) {
        console.warn('[pdp-latest-term] getFilteredCampusTerm failed; will try metaobjects only', e);
        if (!Array.isArray(window.__pdpDdcsCampuses)) cacheFilteredCampusTermLists([], []);
      }

      if (!campusRow) {
        campusRow = await fetchDdcsCampusByEntityIdViaMetaobjects(cStr);
        if (campusRow) campusFrom = 'metaobjects';
      }
      if (!termRow) {
        termRow = await fetchDdcsTermByTermIdViaMetaobjects(tStr);
        if (termRow) termFrom = 'metaobjects';
      }

      window.__pdpDdcsCampusForRental = campusRow;
      window.__pdpDdcsTermForRental = resolveTermRowForRentalRefund(termRow, latestTerm);
  
    } catch (err) {
      console.warn('[pdp-latest-term] Failed to resolve campus / term rows', err);
      window.__pdpDdcsCampusForRental = null;
      window.__pdpDdcsTermForRental = null;
    }
  }

  async function logLatestTermOnPdp() {
    try {
      // Always cache campus/term catalogs for Access program-name joins (multi-campus).
      try {
        const { campuses, terms } = await fetchFilteredCampusTermLists();
        cacheFilteredCampusTermLists(campuses, terms);
      } catch (e) {
        console.warn('[pdp-latest-term] getFilteredCampusTerm cache warm failed', e);
        if (!Array.isArray(window.__pdpDdcsCampuses)) cacheFilteredCampusTermLists([], []);
      }

      const term = await getLatestTermByRentalEndDate();
      if (term) {
        await logResolvedDdcsMetaobjects(term);
      } else {
        window.__pdpDdcsCampusForRental = null;
        window.__pdpDdcsTermForRental = null;
      }
    } catch (err) {
      console.error('[pdp-latest-term]', err);
      window.__pdpDdcsCampusForRental = null;
      window.__pdpDdcsTermForRental = null;
    }
  }

  async function bootstrapPdpDdcsRentalContext() {
    try {
      await logLatestTermOnPdp();
    } finally {
      resolveDdcsRentalReady();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void bootstrapPdpDdcsRentalContext();
    });
  } else {
    void bootstrapPdpDdcsRentalContext();
  }

  window.getLatestTermByRentalEndDate = getLatestTermByRentalEndDate;
  window.fetchFilteredCampusTermLists = fetchFilteredCampusTermLists;
  window.fetchDdcsCampusByEntityIdViaMetaobjects = fetchDdcsCampusByEntityIdViaMetaobjects;
  window.fetchDdcsTermByTermIdViaMetaobjects = fetchDdcsTermByTermIdViaMetaobjects;
})();
